import React, { createContext, useCallback, useContext, useState } from "react";

import { HookResult } from "@fartherfinance/frontend/api/Types";

import GlobalPollingComponent, {
  UseGlobalPollingOptions,
} from "./GlobalPollingComponent";

interface StartPolling {
  <ID, R, T extends (...args: any) => HookResult<R>>(
    pollSessionId: ID,
    options: UseGlobalPollingOptions<T>,
    onSuccess?: (result: HookResult<R>) => void,
    onError?: (error: HookResult<R>) => void
  ): void;
}
interface GlobalPollingContextType {
  startPolling: StartPolling;
  pollingSessions: Map<string, UseGlobalPollingOptions<any>>;
  isPollingSessionActive: <ID>(pollSessionId: ID) => boolean;
  stopPolling: <ID>(pollSessionId: ID) => void;
}

const GlobalPollingContext = createContext<GlobalPollingContextType | null>(
  null
);

export const useGlobalPollingContext = () => {
  const context = useContext(GlobalPollingContext);
  if (!context) {
    throw new Error(
      "useGlobalPollingContext must be used within GlobalPollingProvider"
    );
  }
  return context;
};

export const GlobalPollingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [pollingSessions, setPollingSessions] = useState(new Map());

  const startPolling: StartPolling = useCallback(
    (pollSessionId, options, onSuccess, onError) => {
      const key = JSON.stringify(pollSessionId);

      if (pollingSessions.has(key)) {
        return;
      }

      setPollingSessions((prev) => {
        const newSessions = new Map(prev);
        newSessions.set(key, { ...options, onSuccess, onError });
        return newSessions;
      });
    },
    [pollingSessions]
  );

  const stopPolling = useCallback(<ID,>(pollSessionId: ID) => {
    setPollingSessions((prev) => {
      const key = JSON.stringify(pollSessionId);

      if (!prev.has(key)) {
        return prev;
      }

      const newSessions = new Map(prev);

      newSessions.delete(key);

      return newSessions;
    });
  }, []);

  const isPollingSessionActive = useCallback(
    <ID,>(pollSessionId: ID): boolean => {
      return pollingSessions.has(JSON.stringify(pollSessionId));
    },
    [pollingSessions]
  );

  return (
    <GlobalPollingContext.Provider
      value={{
        startPolling,
        stopPolling,
        pollingSessions,
        isPollingSessionActive,
      }}
    >
      {Array.from(pollingSessions.entries()).map(
        ([pollSessionId, pollingOptions]) => (
          <GlobalPollingComponent
            key={pollSessionId}
            pollingOptions={pollingOptions}
            onSuccess={pollingOptions.onSuccess}
            onError={pollingOptions.onError}
          />
        )
      )}
      {children}
    </GlobalPollingContext.Provider>
  );
};
